import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import type { ArtifactDef } from '@/lib/artifactCatalog'
import type { Artifact } from '@/types/api'

export type ArtifactCardState = 'idle' | 'generating' | 'completed' | 'failed'

/**
 * Backend currently emits `ready` on `Artifact.status` for finished
 * artifacts — the OpenAPI shape predates the `completed` rename. Map
 * any `ready` we see onto our local `completed` state so the card
 * surfaces View/Regenerate instead of falling back to Generate.
 */
function normalizeArtifactState(
  status: string | undefined,
  busy: boolean
): ArtifactCardState {
  if (busy) return 'generating'
  if (!status) return 'idle'
  if (status === 'ready' || status === 'completed') return 'completed'
  if (status === 'failed') return 'failed'
  if (status === 'generating') return 'generating'
  return 'idle'
}

interface ArtifactCardProps {
  def: ArtifactDef
  /** Latest artifact for this type, if any. Drives the visible state. */
  artifact: Artifact | null
  /** True while a Generate / Regenerate POST is in flight. */
  busy: boolean
  onGenerate: () => void
  onView: () => void
  onRetry: () => void
}

/**
 * Single tile in the Business Artifacts grid. State is derived from the
 * latest `Artifact` for this type:
 *   - none / failed → Generate
 *   - generating → spinner, disabled CTA
 *   - completed → Regenerate (secondary) + View (primary)
 *
 * `contextVersion` shows under the title when a completed artifact exists,
 * so the user can tell what context produced the file.
 */
export function ArtifactCard({
  def,
  artifact,
  busy,
  onGenerate,
  onView,
  onRetry,
}: ArtifactCardProps) {
  const state: ArtifactCardState = normalizeArtifactState(
    artifact?.status,
    busy
  )

  return (
    <article
      data-testid={`artifact-card-${def.type}`}
      data-state={state}
      className={cn(
        'flex flex-col rounded-lg border bg-bg-primary p-5 gap-3',
        state === 'failed'
          ? 'border-error/40'
          : state === 'completed'
            ? 'border-success/40'
            : 'border-border-default hover:border-border-hover transition-colors'
      )}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-display font-semibold text-text-primary">
            {def.label}
          </h3>
          {artifact?.contextVersion != null && (
            <p className="mt-0.5 text-[11px] font-mono text-text-tertiary">
              Generated from context v{artifact.contextVersion}
            </p>
          )}
        </div>
        <StatusBadge state={state} />
      </header>

      <p className="text-xs text-text-secondary leading-relaxed flex-1">
        {def.description}
      </p>

      <ArtifactCardActions
        state={state}
        onGenerate={onGenerate}
        onView={onView}
        onRetry={onRetry}
      />
    </article>
  )
}

function StatusBadge({ state }: { state: ArtifactCardState }) {
  if (state === 'generating') {
    return (
      <span
        data-testid="artifact-status-generating"
        className="inline-flex items-center gap-1 text-[11px] font-mono text-info shrink-0"
      >
        <Loader2 size={12} className="animate-spin" />
        Generating…
      </span>
    )
  }
  if (state === 'completed') {
    return (
      <span
        data-testid="artifact-status-completed"
        className="inline-flex items-center gap-1 text-[11px] font-mono text-success shrink-0"
      >
        <CheckCircle2 size={12} />
        Ready
      </span>
    )
  }
  if (state === 'failed') {
    return (
      <span
        data-testid="artifact-status-failed"
        className="inline-flex items-center gap-1 text-[11px] font-mono text-error shrink-0"
      >
        <AlertCircle size={12} />
        Failed
      </span>
    )
  }
  return null
}

interface ActionsProps {
  state: ArtifactCardState
  onGenerate: () => void
  onView: () => void
  onRetry: () => void
}

function ArtifactCardActions({
  state,
  onGenerate,
  onView,
  onRetry,
}: ActionsProps) {
  if (state === 'generating') {
    return (
      <Button size="sm" disabled variant="secondary">
        Generating…
      </Button>
    )
  }
  if (state === 'failed') {
    return (
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    )
  }
  if (state === 'completed') {
    return (
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={onView} data-testid="artifact-view">
          View
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onGenerate}
          data-testid="artifact-regenerate"
        >
          Regenerate
        </Button>
      </div>
    )
  }
  return (
    <Button size="sm" onClick={onGenerate} data-testid="artifact-generate">
      Generate
    </Button>
  )
}
